// Imports for external libraries go here.
import React, { FC } from 'react';
import clsx from 'clsx';
import { Badge, Button, Icon, Image, Types, Text, RichText } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { constants } from '../../modules/utils/constants';
import { getFallbackImage } from '../../modules/utils';
import { PartnerCardProps } from './PartnerCard.types';
import { StyledPartnerCard } from './PartnerCard.styles';

// Use named rather than default exports.
export const PartnerCard: FC<PartnerCardProps> = props => {
  const clientEnvVars = useClientEnvVarsStore(state => state.envVarsObject);
  const { ACCEPT_LANGUAGE: currentLocale } = clientEnvVars;

  return (
    <StyledPartnerCard
      data-testid="account-PartnerCard"
      data-component-name="o-account-PartnerCard"
      className={clsx(`d-flex ${props?.styleclass}`)}
    >
      <Button
        isLink
        testId="partnerCardBtn"
        href={props?.ctaLink}
        target={props?.openInANewTab ? '_blank' : '_self'}
        aria-label={props?.ctaLink}
        trackingProperties={props?.trackingProperties}
        custom_click_track_value={props?.clickTrackValue}
        rel={`${props?.openInANewTab ? 'noopener noreferrer' : ''}`}
        className={clsx(
          `${`partnercard partnercard_chevron d-flex flex-column ${
            props?.openInANewTab === true ? 'icon-arrow-right-external' : 'icon-arrow-right'
          }`}`
        )}
      >
        {
          <div className="partnercard_container d-flex flex-column">
            <div className="partnercard_image">
              <Image
                altText={props?.imageAltText ?? props?.featuredDynamicImage?.altText}
                renditions={props?.featuredDynamicImage?.renditions}
                dynamic={props?.featuredDynamicImage?.dynamic}
                defaultImageURL={
                  props?.image ?? props?.featuredDynamicImage?.assetPath ?? getFallbackImage(currentLocale).Classic
                }
              />
              {props?.logoImage && (
                <Image
                  altText={props?.logoImageAltText ?? props?.featuredDynamicLogoImage?.altText}
                  renditions={props?.featuredDynamicLogoImage?.renditions}
                  dynamic={props?.featuredDynamicLogoImage?.dynamic}
                  defaultImageURL={
                    props?.logoImage ??
                    props?.featuredDynamicLogoImage?.assetPath ??
                    getFallbackImage(currentLocale).Classic
                  }
                  customClass="partnercard_brandlogo"
                />
              )}
            </div>
            <div className="partnercard__body px-4 py-5">
              <div className="partnercard_overlay overlay p-3 d-flex justify-content-between align-items-center">
                {props?.badgeText && (
                  <Badge
                    badgeVariation={Types.BadgeVariation.Overlay}
                    badgeText={props?.badgeText ? props?.badgeText : ''}
                    badgeIcon={props?.badgeIcon?.split(':')[1] || props?.badgeIcon}
                    customClass="partnercard_badge p-2 align-items-start"
                  />
                )}
                {props?.socialMediaIcon && (
                  <Button
                    className="partnercard_socialicon m-icon-button-primary mr-2"
                    children={<Icon iconClass={`icon-${props?.socialMediaIcon} icon-l`} />}
                  />
                )}
              </div>
              <div className="d-flex flex-column">
                <RichText
                  customClass="t-font-s partnercard_heading mb-1"
                  text={props?.header || ''}
                  componentId={'partnerCardTitle'}
                />
                <div className="partnercard_detail d-flex">
                  <RichText
                    customClass={`partnercard_description m-ellipsis-2lines ${props.titleFontSizeClass} col-10 px-0 t-subtitle-xl`}
                    text={props?.description?.slice(0, constants.PARTNER_CARD_SUB_HEADER_LIMIT) || ''}
                    componentId={'partnerCardDescription'}
                  />
                  <span className="col-2"></span>
                </div>
              </div>
            </div>
          </div>
        }
      </Button>
    </StyledPartnerCard>
  );
};
